import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef } from 'react';
import { useUXPContext } from '@core/context/useUXPContext';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import { Dropdown } from '@widget/popups/Dropdown';
import { hasValue } from '@utils';
import PaginationButton from './PaginationButton';
import { SkeletonLoader } from '@widget/loaders';
/**
 * @export
 *
 * Pagination controls — compact prev/next with a page-size dropdown.
 * Renders as a flat set of controls; layout (left/right slots) is handled by the parent.
 *
 * @example
 * ```
 * <PaginationComponent
 *   total={total}
 *   pageSize={pageSize}
 *   page={page}
 *   onPageChange={setPage}
 *   onPageSizeChange={ps => { setPageSize(ps); setPage(1); }}
 * />
 * ```
 *
 * @partOf TableComponent
 */
const PaginationComponent = ({ total, pageSize, page, dataLength = 0, loading, onPageChange, onPageSizeChange, allowPageSizeChange = true, onRefresh, leading, countOnly, }) => {
    const context = useUXPContext();
    const pageSizeRef = useRef(null);
    const hasTotalCount = hasValue(total) && total > 0;
    const maxPage = hasTotalCount && pageSize > 0 ? Math.ceil(total / pageSize) : 0;
    const start = dataLength > 0 ? (page - 1) * pageSize + 1 : 0;
    const end = (page - 1) * pageSize + dataLength;
    const pageSizeOptions = [10, 20, 50, 100];
    // Only skeleton on the initial load (no data yet). During prev/next paging the
    // previous page's rows are still present, so keep the controls visible - the count
    // text updates in place when the new page lands.
    if (loading && dataLength === 0) {
        return _jsx(SkeletonLoader, { width: "15rem", height: "1rem" });
    }
    if (countOnly) {
        return (_jsxs("div", { className: 'uxpcore_pagination__container', children: [leading, onRefresh && (_jsx(PaginationButton, { onClick: onRefresh, disabled: !!loading, title: context.$L('uxp-core.text.refresh'), children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'rotate-right'] }) })), _jsx("div", { className: "uxpcore_pagination__count uxpcore_pagination__count--static", children: hasTotalCount ? context.$L('uxp-core.text.x-of-total', { loaded: dataLength, total }) : dataLength })] }));
    }
    return (_jsxs("div", { className: 'uxpcore_pagination__container', children: [leading, onRefresh && (_jsx(PaginationButton, { onClick: onRefresh, disabled: !!loading, title: context.$L('uxp-core.text.refresh'), children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'rotate-right'] }) })), allowPageSizeChange ? (_jsx(Dropdown, { ref: pageSizeRef, position: "top-left", trigger: _jsx("div", { className: "uxpcore_pagination__count", children: hasTotalCount
                        ? context.$L('uxp-core.text.pagination-counts', { start, end, total })
                        : `${start} - ${end}` }), content: _jsxs(_Fragment, { children: [_jsx("div", { className: "uxpcore_pagination__pagesize-title", children: context.$L('uxp-core.text.records-per-page') }), pageSizeOptions.map(ps => (_jsx("div", { className: `uxpcore_pagination__pagesize${pageSize === ps ? ' uxpcore_pagination__pagesize--active' : ''}`, onClick: () => { var _a; onPageSizeChange(ps); (_a = pageSizeRef.current) === null || _a === void 0 ? void 0 : _a.close(); }, children: context.$L('uxp-core.text.x-records', { count: ps }) }, ps)))] }) })) : (_jsx("div", { className: "uxpcore_pagination__count", children: hasTotalCount
                    ? context.$L('uxp-core.text.pagination-counts', { start, end, total })
                    : `${start} - ${end}` })), _jsx(PaginationButton, { onClick: () => onPageChange(Math.max(page - 1, 1)), ariaLabel: context.$L('uxp-core.text.previous'), disabled: page === 1, children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'chevron-left'] }) }), _jsx(PaginationButton, { onClick: () => onPageChange(page + 1), ariaLabel: context.$L('uxp-core.text.next'), disabled: hasTotalCount
                    ? page >= maxPage
                    : dataLength < pageSize, children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'chevron-right'] }) })] }));
};
export default PaginationComponent;
